import { Box, Typography, InputBase, Select } from '@mui/material'
import React, { useState, useMemo, useEffect } from 'react'
import ListSubheader from '@mui/material/ListSubheader'
import AdxInput from 'components/custom/AdxInput'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import { useDebouncedEffect } from 'services/hooks/useDebouncedEffect'
import { useTheme } from '@mui/material/styles'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import FlagIcon from '@mui/icons-material/Flag'
import PhoneIcon from '@mui/icons-material/Phone'
import AdxErrorMessage from '../../custom/AdxErrorMessage'
import { ErrorMessage } from 'formik'
import axios from 'axios'
import CircularProgress from '@mui/material/CircularProgress'
import { countries } from 'services/api/dropdown'
import { useQuery } from 'react-query'

const getAllCountries = async (fields = 'name,flags,cca2') => {
  try {
    const res = await axios.get(`https://restcountries.com/v3.1/all?fields=${fields}`)
    if (Array.isArray(res?.data)) {
      return res.data
    } else {
      return []
    }
  } catch (error) {
    return []
  }
}

const getCountryFlagUrl = (countryCode, thirdPartyList = []) => {
  const code = countryCode?.toLowerCase()

  if (!code) {
    return ''
  }

  if (Array.isArray(thirdPartyList)) {
    const country = thirdPartyList.find((element) => element.cca2 === countryCode)

    if (country?.flags?.png) {
      return country.flags.png
    }
  }

  return `https://flagcdn.com/w40/${code}.png`
}

const AdxPhoneNumber = ({
  // Dropdown
  dropdownValue,
  dropdownName,
  onDropdownChange,
  dropdownDisabled = false,
  sxDropdown,
  dropdownErrorMessage,
  dropdownIsErrorShown,
  // Input
  inputValue,
  inputName,
  onInputChange,
  inputDisabled = false,
  sxInput,
  inputErrorMessage,
  inputIsErrorShown,
  // Label
  label,
  sxLabel,
  // Others
  size,
  selectedCountries, // accept an array of country names that want to be filtered. Example: selectedCountries = ['Malaysia', 'Indonesia', 'Singapore'],
  filters = ['country_name', 'phone_code'],
  modalSearch = 'Search country name or code',
  sx,
  sxBox,
  ...rest
}) => {
  const [searchFilterValue, setSearchFilterValue] = useState('')
  const [countryLists, setCountryLists] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  // state and function for AdxDropdown components
  const [selectInternalValue, setSelectInternalValue] = useState({
    phone_code: '', // idd is used as value
    country_name: '',
    country_flag: '',
    country_code: '',
    id: 0,
  })

  const { data: countriesList } = useQuery('countriesList', async () => {
    const { data } = await countries()
    return data
  })

  const { data: thirdPartyList } = useQuery('thirdPartyPhoneList', async () => {
    const thirdPartyData = await getAllCountries('name,flags,cca2,idd')
    return thirdPartyData
  })

  const handleChangeSearchFilter = (event) => {
    event.preventDefault()
    setSearchFilterValue(event.target.value)
  }

  const handleSetSelectInternalValue = (event) => {
    setSelectInternalValue(event.target.value)
  }

  const fetchCountries = async () => {
    if (dropdownValue || dropdownValue === null) {
      setIsLoading(true)
      if (countriesList?.length) {
        const _countries = countriesList
          .filter((option) => {
            if (Boolean(selectedCountries)) {
              for (let key in selectedCountries) {
                if (option.name.toLowerCase() === selectedCountries[key].toLowerCase()) {
                  return true
                }
              }
              return false
            } else {
              return option.country_code !== 'IL'
            }
          })
          .map((counties) => {
            return {
              id: counties.id,
              phone_code: `+${counties.phone_code}`,
              country_name: counties.name,
              country_code: counties.country_code,
              country_flag: getCountryFlagUrl(counties.country_code, thirdPartyList),
            }
          })
          // const _countries = thirdPartyList.map(r => {
          //     const idd = `${r.idd.root}${r.idd.suffixes?.length === 1 ? r.idd.suffixes[0] : ''}`;
          //     return {
          //         phone_code: idd, // idd is used as value
          //         country_name: r.name.common,
          //         country_flag: r.flags.png,
          //         country_code: r.cca2.toLowerCase(),
          //     }
          // })
          .filter((c) => c.phone_code)
          .sort((a, b) => {
            return a.country_name.localeCompare(b.country_name)
          })
        setCountryLists(_countries)
        setCountryOptions(_countries)

        // if(isFocused) setCountries(_countries);

        // if(defaultCountry) {
        //     const defaultCode = _countries.find(c => c.code === defaultCountry);
        //     setCountryCode(defaultCode);
        // }
        if (dropdownValue) {
          let number = typeof dropdownValue === 'string' ? parseInt(dropdownValue) : dropdownValue
          const defaultCountry = _countries.find((one) => one.id === number)
          setSelectInternalValue(defaultCountry)
        }
      }
      setIsLoading(false)
    }
  }

  const handleChangesManually = (e) => {
    handleSetSelectInternalValue(e)
    onDropdownChange(JSON.stringify(e.target.value.id))
  }

  const { componentHeight, palette } = useTheme()

  let fontSize
  let height
  let paddingX

  switch (size) {
    case 'standard':
      fontSize = 16
      paddingX = 16
      height = 55
      break

    case 'large':
      fontSize = 20
      paddingX = 20
      height = 75
      break

    default:
      fontSize = 16
      paddingX = 16
      height = 55
  }

  const defaultProps = () => {
    return {
      fontSize: `${fontSize}px !important`,
      paddingX: `${paddingX}px`,
      height,
      width: '100%',
      marginRight: '1px',
      borderTopRightRadius: `${height * 0.1}px`,
      '&.MuiInputBase-input': {
        fontSize: 50,
      },
      borderBottomRightRadius: `${height * 0.1}px`,
      outlineColor: inputIsErrorShown ? palette.error.main : null,
      '&.MuiInputBase-root': {
        '&.Mui-focused': {
          outlineColor: inputIsErrorShown ? palette.error.main : null,
        },
      },
    }
  }

  const MenuProps = () => {
    return {
      height,
      borderTopRightRadius: '0px',
      borderBottomRightRadius: '0px',
      borderTopLeftRadius: `${height * 0.1}px`,
      borderBottomLeftRadius: `${height * 0.1}px`,
      '&. MuiMenu-root': {
        color: 'alternate.main',
      },
      '&.Mui-disabled': {
        backgroundColor: 'gray-inactive-button.main',
        color: '#FFFFFF',
      },
      outlineColor: dropdownIsErrorShown ? palette.error.main : null,
      '&.MuiOutlinedInput-root': {
        '&.Mui-focused': {
          outlineColor: dropdownIsErrorShown ? palette.error.main : null,
        },
      },
    }
  }

  const displayedOptions = useMemo(() => {
    return countryLists.filter((option) => {
      for (let key in filters) {
        if (
          option[filters[key]] &&
          option[filters[key]].toString().toLowerCase().indexOf(searchFilterValue.toLowerCase()) >
            -1
        ) {
          return true
        }
      }
      return false
    })
  }, [searchFilterValue]) // eslint-disable-line react-hooks/exhaustive-deps

  useDebouncedEffect(
    () => {
      setCountryOptions(displayedOptions)
    },
    [displayedOptions],
    200
  )

  useEffect(() => {
    if (countriesList && thirdPartyList) {
      fetchCountries()
    }
  }, [countriesList, thirdPartyList, dropdownValue]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box display={'flex'} flexDirection={'column'} rowGap={label ? 1 : null} sx={{ ...sx }}>
      {label ? (
        <Typography variant="subtitle1" color={'dark-text.main'} sx={{ ...sxLabel }}>
          {label}
        </Typography>
      ) : null}
      <Box>
        <Box display={'flex'} flexDirection={'row'} sx={{ ...sxBox }}>
          <Select
            color="alternate"
            value={selectInternalValue}
            name={dropdownName}
            onChange={handleChangesManually}
            disabled={dropdownDisabled}
            IconComponent={KeyboardArrowDownIcon}
            MenuProps={{
              sx: {
                '&.MuiMenu-root': {
                  my: 1.25,
                  maxHeight: '440px',
                },
              },
              autoFocus: false,
            }}
            renderValue={(option) => {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {!isLoading ? (
                    <>
                      {option.country_flag ? (
                        <Box
                          as="img"
                          src={option.country_flag}
                          sx={{
                            backgroundColor: 'gray-background.main',
                            width: '20px',
                            height: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '10%',
                            mx: 1,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            // backgroundColor: "gray-background.main",
                            width: '20px',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '10%',
                            mx: 1,
                          }}
                        >
                          <PhoneIcon sx={{ fontSize: 18, marginTop: '2px' }} color="alternate" />
                        </Box>
                      )}
                      <Typography variant="caption">{option.phone_code}</Typography>
                    </>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <CircularProgress color="alternate" size={20} />
                    </Box>
                  )}
                </Box>
              )
            }}
            sx={{
              ...MenuProps(),
              height: componentHeight.standard,
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: 0,
              },
              '&.Mui-disabled': {
                backgroundColor: 'gray-background.main',
                color: '#FFFFFF',
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 0,
                },
              },
              ...sxDropdown,
            }}
            {...rest}
          >
            {/* Filter search bar here. Only show if display all countries */}
            {Boolean(!selectedCountries) && (
              <ListSubheader sx={{ p: 2 }}>
                <AdxInput
                  value={searchFilterValue}
                  onChange={handleChangeSearchFilter}
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') {
                      // Prevents autoselecting item while typing (default Select behaviour)
                      e.stopPropagation()
                    }
                  }}
                  autoFocus
                  size="standard"
                  placeholder={modalSearch}
                  sx={{
                    backgroundColor: 'gray-background.main',
                    outlineColor: 'white',
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <SearchIcon sx={{ mx: 1 }} />
                    </InputAdornment>
                  }
                />
              </ListSubheader>
            )}
            {!isLoading ? (
              countryOptions.map((item) => (
                <AdxMenuItem key={item?.id} value={item}>
                  <Box
                    sx={{
                      width: '25vw',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {item.country_flag ? (
                        <Box
                          as="img"
                          sx={{
                            backgroundColor: 'gray-background.main',
                            width: '20px',
                            height: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '10%',
                            mx: 1,
                          }}
                          src={item.country_flag}
                        />
                      ) : (
                        <Box
                          sx={{
                            // backgroundColor: "gray-background.main",
                            width: '20px',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '10%',
                            mx: 1,
                          }}
                        >
                          <FlagIcon fontSize="20px" color="alternate" />
                        </Box>
                      )}
                      <Typography variant="body1">{item.country_name}</Typography>
                    </Box>
                    <Typography variant="caption">{item.phone_code}</Typography>
                  </Box>
                </AdxMenuItem>
              ))
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="150px">
                <CircularProgress color="alternate" />
              </Box>
            )}
          </Select>
          <InputBase
            {...rest}
            sx={{
              ...defaultProps(),
              ...sxInput,
              marginLeft: '1px',
              height: componentHeight.standard,
            }}
            value={inputValue}
            onChange={onInputChange}
            name={inputName}
            disabled={inputDisabled}
            type="tel"
          />
        </Box>
        {inputIsErrorShown && dropdownIsErrorShown ? (
          <AdxErrorMessage>Phone Number and Phone Code is required</AdxErrorMessage>
        ) : inputName && inputIsErrorShown ? (
          <ErrorMessage name={inputName} component={AdxErrorMessage} />
        ) : (
          dropdownName &&
          dropdownIsErrorShown && <ErrorMessage name={dropdownName} component={AdxErrorMessage} />
        )}
      </Box>
    </Box>
  )
}

export default AdxPhoneNumber
